import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, MapPin, RefreshCw, Siren } from 'lucide-react';
import { Link } from 'react-router-dom';

import { businessDeliveryOperationsService } from '../../services/businessDeliveryOperationsService';

// APP-DEL-019: problems Riders report from the road. They are a heads-up to
// the store, not a delivery outcome: nothing here changes a stop, a trip or who
// holds a parcel. A delivery that did not happen is decided in Failed stops.

export const REPORT_LABELS = Object.freeze({
  SAFETY_CONCERN: 'Safety concern',
  VEHICLE_ISSUE: 'Vehicle problem',
  NAVIGATION_ISSUE: 'Route or navigation',
  CUSTOMER_UNREACHABLE: 'Customer unreachable',
  ADDRESS_ISSUE: 'Address problem',
  PARCEL_ISSUE: 'Parcel problem',
  DEVICE_OR_NETWORK: 'Phone or signal',
  AUTHORITY_CONTESTED: 'Controlling phone changed',
  OTHER: 'Other problem',
});

const PHASE_LABELS = Object.freeze({
  READY: 'before leaving the store',
  EN_ROUTE: 'on the way to a customer',
  AT_STOP: 'at a stop',
  AFTER_ARRIVAL: 'at the customer',
  AUTHORITY_CONTESTED: 'while switching phones',
  OTHER: 'during the trip',
});

function when(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

export default function RiderReportsPanel({ businessId, refreshRevision = 0 }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading');

  const load = useCallback(async (signal) => {
    setState('loading');
    try {
      const data = await businessDeliveryOperationsService.riderReports(businessId, { signal });
      if (signal?.aborted) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setState('ready');
    } catch {
      if (!signal?.aborted) setState('error');
    }
  }, [businessId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshRevision]);

  const urgent = items.filter((item) => item.urgent);
  const rest = items.filter((item) => !item.urgent);

  return (
    <section className="rounded-panel border border-default bg-surface p-4 sm:p-5" aria-labelledby="rider-reports-title" data-testid="rider-reports">
      <div>
        <div>
          <h2 id="rider-reports-title" className="font-semibold">Road reports</h2>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
            Problems your Riders told you about from the road in the last two weeks. A report does not change any delivery;
            a delivery that did not happen is decided in <Link to="/business/delivery?view=exceptions" className="underline">Failed stops</Link>.
          </p>
        </div>
      </div>

      {state === 'loading' ? <p role="status" className="mt-4 flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading reports…</p> : null}
      {state === 'error' ? <div role="alert" className="mt-4 rounded-panel border border-danger bg-danger-subtle p-4 text-sm text-danger"><p>Reports could not be loaded. Check your connection and try again.</p><button type="button" onClick={() => load()} className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-control border border-danger px-3 font-semibold hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><RefreshCw className="h-4 w-4" aria-hidden="true" />Try again</button></div> : null}
      {state === 'ready' && !items.length ? <p className="mt-4 rounded-2xl bg-neutral-50 p-4 text-sm text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">No Rider has reported a problem in the last two weeks.</p> : null}

      {state === 'ready' && items.length ? (
        <ul className="mt-4 space-y-3">
          {[...urgent, ...rest].map((item) => (
            <li key={item.reportId} data-testid="rider-report" className={`rounded-2xl border p-4 ${item.urgent ? 'border-danger bg-danger-subtle' : 'border-neutral-200 dark:border-neutral-800'}`}>
              <div className="flex flex-wrap items-center gap-2">
                {item.urgent
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-danger px-2.5 py-1 text-xs font-semibold text-on-accent"><Siren className="h-3.5 w-3.5" aria-hidden="true" /> {REPORT_LABELS[item.reasonCode]}</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"><AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> {REPORT_LABELS[item.reasonCode] || 'Problem'}</span>}
                <span className="text-sm font-semibold">{item.riderName || 'Rider'}</span>
                <span className="text-xs text-neutral-500">{when(item.reportedAt)}</span>
              </div>
              <p className="mt-2 text-sm">
                Reported {PHASE_LABELS[item.phase] || 'during the trip'}
                {item.tripId ? <> on <Link to={`/business/delivery?view=trips&trip=${item.tripId}`} className="font-medium underline">trip #{item.tripId}</Link></> : null}
                {item.orderReference ? `, stop ${item.stopSequence ?? ''} (${item.orderReference})` : ''}.
              </p>
              {item.note ? <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-100">“{item.note}”</p> : null}
              {item.location ? (
                <a href={`https://www.google.com/maps/search/?api=1&query=${item.location.latitude},${item.location.longitude}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Where the Rider was
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
